<template>
  <div class="proj-page">
    <!-- 步骤条 -->
    <div class="stepper g-container">
      <div
        v-for="(s, i) in steps"
        :key="i"
        :class="['step', tab >= i + 1 && 'is-active', tab > i + 1 && 'is-done']"
      >
        <div class="num">{{ i + 1 }}</div>
        <span class="label">{{ s }}</span>
        <div class="line" v-if="i < steps.length - 1"></div>
      </div>
    </div>

    <!-- 列表视图 -->
    <div v-show="tab === 1" class="proj-list-pane g-container">
      <div class="search-bar">
        <el-input
          v-model="keyword"
          placeholder="按标段名称 / 编号搜索"
          @keyup.enter.native="search"
        >
          <i slot="prefix" class="el-icon-search"></i>
        </el-input>
        <button class="g-btn g-btn-default" @click="filterOpen = !filterOpen">
          {{ filterOpen ? '收起' : '高级' }}筛选
        </button>
        <button class="g-btn g-btn-primary" @click="search">查询</button>
      </div>

      <transition name="slide-down">
        <div v-show="filterOpen" class="filter-panel">
          <div class="filter-row">
            <label>产品类型</label>
            <div class="chips">
              <button
                v-for="t in tabList"
                :key="t.code"
                :class="['chip', code === t.code && 'is-active']"
                @click="selectCode(t.code)"
              >{{ t.name }}</button>
            </div>
          </div>
          <div class="filter-row">
            <label>来源区域</label>
            <div class="chips">
              <button :class="['chip', city === '' && 'is-active']" @click="changecity('')">全部</button>
              <button
                v-for="c in cityList"
                :key="c.id"
                :class="['chip', city === c.id && 'is-active']"
                @click="changecity(c.id)"
              >{{ c.name }}</button>
            </div>
          </div>
          <div v-if="countyList.length" class="filter-row">
            <label></label>
            <div class="chips">
              <button :class="['chip', county === '' && 'is-active']" @click="changecounty('')">全部</button>
              <button
                v-for="c in countyList"
                :key="c.id"
                :class="['chip', county === c.id && 'is-active']"
                @click="changecounty(c.id)"
              >{{ c.name }}</button>
            </div>
          </div>
        </div>
      </transition>

      <div v-if="list.length" class="proj-grid">
        <article
          v-for="(item, i) in list"
          :key="i"
          class="proj-card g-card"
        >
          <!-- 头部：项目编号 + 项目名称 + 状态 -->
          <header class="card-head">
            <div class="prj-title">
              <!-- 第一行：交易中心标签 + 标段编号 + 产品类型 -->
              <div class="prj-meta-row">
                <span class="region-tag" v-if="item.areaName || item.platName || item.tradeCenter || item.jyCenter">
                  <i class="el-icon-place"></i>
                  {{ item.areaName || item.platName || item.tradeCenter || item.jyCenter }}
                </span>
                <span class="prj-no-inline">
                  <i class="el-icon-document"></i>
                  <label>标段编号</label>
                  <em>{{ item.prjNo }}</em>
                </span>
                <span class="type-chip" v-if="item.typeName">{{ item.typeName }}</span>
              </div>
              <!-- 第二行：项目名称（换行显示，字号小一号） -->
              <h3 class="prj-name">{{ item.prjName }}</h3>
            </div>
            <span :class="['status-tag', isExpired(item) ? 'is-expired' : 'is-open']">
              <span class="dot"></span>
              {{ isExpired(item) ? '已截止' : '申请中' }}
            </span>
          </header>

          <!-- 核心数据：3列卡片 -->
          <div class="card-stats">
            <div class="stat stat-amount">
              <div class="stat-label">
                <i class="el-icon-wallet"></i>保证金
              </div>
              <div class="stat-value amount-value">
                ¥ <span class="num">{{ formatMoney(item.prjEarMoney) }}</span>
              </div>
            </div>
            <div class="stat">
              <div class="stat-label">
                <i class="el-icon-time"></i>开标时间
              </div>
              <div class="stat-value date-value">
                <i class="el-icon-date"></i>{{ item.tendStDate }}
              </div>
            </div>
            <div class="stat">
              <div class="stat-label">
                <i class="el-icon-alarm-clock"></i>保证金截止
              </div>
              <div class="stat-value date-value" :class="{ 'is-expired': isExpired(item) }">
                <i class="el-icon-date"></i>{{ item.prjEndEarDate }}
              </div>
            </div>
          </div>

          <!-- 底部操作 -->
          <footer class="card-foot">
            <button
              class="g-btn g-btn-primary apply-btn"
              :disabled="isExpired(item)"
              @click="toApply(item)"
            >
              <i class="el-icon-s-promotion"></i>立即申请
            </button>
          </footer>
        </article>
      </div>

      <div v-else class="g-empty">
        <img src="@/assets/noData.png" />
        <p>暂无符合条件标段</p>
      </div>

      <el-pagination
        v-if="list.length"
        background
        layout="prev, pager, next"
        :total="total"
        :current-page.sync="pageNum"
        @current-change="fetch"
        class="pager"
      />
    </div>

    <!-- 申请信息 -->
    <div v-show="tab === 2" class="apply-pane g-container">
      <apply-form
        ref="applyForm"
        :info="info"
        :co-id="coId"
        :code="code"
        :product-list="productList"
        @next="goNext"
        @back="tab = 1"
      />
    </div>

    <!-- 信息确认 -->
    <div v-show="tab === 3" class="apply-pane g-container">
      <div class="confirm-card">
        <h3>请确认申请信息</h3>
        <div class="confirm-grid">
          <div><label>标段编号</label><span>{{ info.prjNo }}</span></div>
          <div><label>保证金</label><span>¥ {{ formatMoney(info.prjEarMoney) }}</span></div>
          <div><label>经办人</label><span>{{ form.name }}</span></div>
          <div><label>身份证</label><span>{{ form.idCard }}</span></div>
          <div><label>手机号</label><span>{{ form.phone }}</span></div>
          <div><label>邮箱</label><span>{{ form.eMail }}</span></div>
        </div>
        <div class="confirm-foot">
          <button class="g-btn g-btn-default" @click="tab = 2">上一步</button>
          <button class="g-btn g-btn-primary" @click="confirmPay">确认提交并支付</button>
        </div>
      </div>
    </div>

    <!-- 支付 -->
    <div v-show="tab === 4" class="pay-pane g-container">
      <div class="pay-card">
        <div v-if="apiRes.payData" class="qr-box">
          <img :src="apiRes.payData" alt="" />
          <p>使用微信扫码支付</p>
          <p class="amount">¥ {{ apiRes.money }}</p>
        </div>
        <p class="pay-tip">完成支付后，可在「我的订单」查看出函进度</p>
        <div class="pay-foot">
          <button class="g-btn g-btn-default" @click="$router.push('/attestation?tab=order')">查看我的订单</button>
          <button class="g-btn g-btn-primary" @click="pay">立即支付</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApplyForm from './ApplyForm'
import { getList, getListNoToken, getCityList, getCityListByToken, getCountyList, getCountyListByToken, getCascaderList, getMapTree, getMapTreeNoToken, getBusMap, getProListByPrjNo, pay, applyState, applyStateNoToken, addInfo, addInfoBook, getAccountState, getProList, getProListByToken } from '@/lib/api/project'
import { getCoList, getAccountState as coState } from '@/lib/api/attestation'

export default {
  name: 'ProjectList',
  components: { ApplyForm },
  data () {
    return {
      tab: 1,
      keyword: '',
      city: '',
      county: '',
      code: '',
      coId: -1,
      filterOpen: false,
      now: Date.now(),
      info: {},
      form: {},
      list: [],
      total: 0,
      pageNum: 1,
      tabList: [],
      cityList: [],
      countyList: [],
      productList: [],
      apiRes: {},
      steps: ['选择标段', '填写信息', '信息确认', '支付出函']
    }
  },
  computed: {
    formatMoney () {
      return (v) => (v ? Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-')
    }
  },
  created () {
    setInterval(() => { this.now = Date.now() }, 1000)
    if (localStorage.getItem('token')) {
      getCityListByToken().then((res) => { if (res.code === 200) this.cityList = res.data })
      getMapTree().then((res) => { if (res.code === 200) this.handleCascader(res.data) })
      getCoList(localStorage.getItem('userId')).then((res) => {
        if (res.code === 200 && res.data && res.data.id) {
          this.coId = res.data.id
        }
      })
    } else {
      getCityList().then((res) => { if (res.code === 200) this.cityList = res.data })
      getMapTreeNoToken().then((res) => { if (res.code === 200) this.handleCascader(res.data) })
    }
    this.fetch({ pageNum: 1 })
  },
  methods: {
    handleCascader (data) {
      this.tabList = (data || []).reduce((acc, p) => acc.concat(p.list || []), [])
      const def = this.$route.query.code
      const target = this.tabList.find((t) => t.code === def) || this.tabList[0]
      if (target) this.code = target.code
    },
    selectCode (c) {
      this.code = c
    },
    isExpired (item) {
      return this.now > new Date(item.prjEndEarDate).getTime()
    },
    fetch (params = { pageNum: 1 }) {
      const fn = localStorage.getItem('token') ? getList : getListNoToken
      fn({
        pageSize: 10,
        tendName: this.keyword,
        tendJur: this.city,
        tendCity: this.county,
        ...params
      }).then((res) => {
        if (res.code === 200) {
          this.list = res.rows || []
          this.total = res.total || 0
        }
      })
    },
    changecity (n) {
      this.city = n
      this.county = ''
      if (n) {
        const fn = localStorage.getItem('token') ? getCountyListByToken : getCountyList
        fn(n).then((res) => { if (res.code === 200) this.countyList = res.data })
      } else {
        this.countyList = []
      }
      this.fetch()
    },
    changecounty (n) { this.county = n; this.fetch() },
    search () { this.pageNum = 1; this.fetch() },
    toApply (item) {
      if (!localStorage.getItem('token')) {
        return this.$alert('请前往登录', '提示', { confirmButtonText: '去登录', callback: () => this.$router.push('/') })
      }
      this.info = item
      this.tab = 2
    },
    goNext (payload) {
      this.form = payload
      this.tab = 3
    },
    confirmPay () {
      // 提交到后端
      const params = {
        ...this.form,
        proNo: this.info.prjDatNo,
        type: this.code,
        prodId: this.productList[0] && this.productList[0].id
      }
      const fn = this.code === 'LYBH' ? addInfoBook : addInfo
      fn(params).then(() => {
        // 调起支付
        this.tab = 4
        this.pay()
      })
    },
    pay () {
      pay({ wayCode: 'WX_NATIVE', prjNo: this.info.prjNo }).then((res) => {
        this.apiRes = (res && res.data) || {}
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/assets/styles/variables.less';

.proj-page { padding: 24px 0 48px; }

.stepper {
  display: flex;
  align-items: center;
  padding: 24px;
  background: #fff;
  border-radius: @radius-lg;
  margin-bottom: 16px;

  .step {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    color: @text-disabled;

    .num {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: @bg-soft;
      color: @text-disabled;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 13px;
      transition: all @duration-base @ease-out;
    }

    .label { font-size: 14px; }
    .line {
      flex: 1;
      height: 1px;
      background: @border-light;
    }

    &.is-active {
      color: @primary;
      .num { background: @primary; color: #fff; }
    }
    &.is-done .num { background: @success; color: #fff; }
  }
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  .el-input { flex: 1; }
  ::v-deep .el-input__inner { border-radius: @radius-full; height: 40px; }
}

.filter-panel {
  background: #fff;
  border-radius: @radius-lg;
  padding: 16px 24px;
  margin-bottom: 16px;

  .filter-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px dashed @border-light;

    &:last-child { border-bottom: none; }

    label {
      width: 80px;
      color: @text-secondary;
      font-size: 13px;
      padding-top: 6px;
    }

    .chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .chip {
        height: 30px;
        padding: 0 14px;
        border-radius: @radius-full;
        border: 1px solid @border-base;
        background: #fff;
        color: @text-secondary;
        font-size: 13px;
        cursor: pointer;
        transition: all @duration-fast @ease-out;

        &:hover { color: @primary; border-color: @primary; }
        &.is-active {
          background: @primary;
          color: #fff;
          border-color: @primary;
        }
      }
    }
  }
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: opacity @duration-base @ease-out, transform @duration-base @ease-out;
}
.slide-down-enter-from, .slide-down-leave-to {
  opacity: 0; transform: translateY(-8px);
}

.proj-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.proj-card {
  background: #fff;
  padding: 20px 24px 20px;
  border: 1px solid @border-light;
  transition: all @duration-base @ease-out;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &:hover {
    border-color: @primary-soft;
    box-shadow: 0 6px 20px rgba(24, 144, 255, 0.08);
    transform: translateY(-2px);
  }

  /* 头部 */
  .card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    .prj-title {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .prj-meta-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      min-width: 0;
    }

    .region-tag {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 10px;
      background: @primary-soft;
      color: @primary;
      border-radius: @radius-sm;
      font-size: 12px;
      flex-shrink: 0;
      order: 1; /* 交易中心标签：永远最前 */

      i { font-size: 12px; }
    }

    .prj-no-inline {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: @text-secondary;
      min-width: 0;
      order: 3; /* 标段编号：永远在标签之后 */

      > i {
        color: @primary;
        font-size: 13px;
        flex-shrink: 0;
      }

      label {
        color: @text-muted;
        flex-shrink: 0;
      }

      em {
        font-style: normal;
        color: @text-base;
        font-family: 'SFMono-Regular', Consolas, monospace;
        font-size: 12px;
      }
    }

    .type-chip {
      flex-shrink: 0;
      display: inline-block;
      padding: 3px 10px;
      background: @accent-soft;
      color: @accent;
      border-radius: @radius-sm;
      font-size: 12px;
      font-weight: 500;
      order: 2; /* 产品类型标签：在区域之后、编号之前 */
    }

    .prj-name {
      margin: 0;
      font-size: 14px;
      font-weight: 500;
      color: @text-base;
      line-height: 1.6;
      max-width: 100%;
      white-space: normal;
      word-break: break-all;
    }

    .status-tag {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: @radius-full;
      font-size: 12px;
      font-weight: 500;

      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
      }

      &.is-open {
        background: @success-soft;
        color: @success;
        .dot { background: @success; }
      }
      &.is-expired {
        background: @warning-soft;
        color: @warning;
        .dot { background: @warning; }
      }
    }
  }

  /* 核心数据 3列 */
  .card-stats {
    display: grid;
    grid-template-columns: 1.2fr 1fr 1fr;
    gap: 12px;
    background: @bg-page;
    border-radius: @radius-md;
    padding: 14px 16px;

    .stat {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 0;
    }

    .stat-label {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: @text-muted;
      font-size: 12px;

      i { font-size: 13px; }
    }

    .stat-value {
      font-size: 14px;
      color: @text-strong;
      font-weight: 500;
    }

    .stat-amount {
      .stat-value { font-size: 13px; }
      .amount-value {
        color: @danger;
        font-weight: 500;
        font-size: 14px;

        .num {
          font-size: 22px;
          font-weight: 700;
          margin-left: 2px;
          font-family: 'DIN Alternate', -apple-system, sans-serif;
        }
      }
    }

    .date-value {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 14px;
      color: @text-base;

      i { color: @primary; font-size: 13px; }

      &.is-expired {
        color: @warning;
        i { color: @warning; }
      }
    }
  }

  /* 底部操作 */
  .card-foot {
    display: flex;
    justify-content: flex-end;
    padding-top: 4px;
    border-top: 1px solid @border-light;
    padding-top: 14px;

    .apply-btn {
      min-width: 120px;
      height: 38px;

      i { margin-right: 4px; }
    }
  }
}

.confirm-card, .pay-card {
  background: #fff;
  border-radius: @radius-lg;
  padding: 32px;
  box-shadow: @shadow-sm;

  h3 {
    margin: 0 0 24px;
    font-size: 18px;
    color: @text-strong;
  }
}

.confirm-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px 24px;

  div {
    display: flex;
    font-size: 14px;
    label { color: @text-muted; margin-right: 6px; }
    span { color: @text-base; }
  }
}

.confirm-foot, .pay-foot {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid @border-light;
}

.pay-card {
  text-align: center;

  .qr-box {
    display: inline-block;
    padding: 24px;
    border: 1px solid @border-light;
    border-radius: @radius-lg;

    img { width: 200px; height: 200px; }
    p { margin: 12px 0 0; color: @text-secondary; }
    .amount {
      font-size: 22px;
      font-weight: 700;
      color: @danger;
    }
  }
  .pay-tip {
    margin-top: 24px;
    color: @text-muted;
    font-size: 13px;
  }
  .pay-foot {
    justify-content: center;
    border-top: none;
    padding-top: 0;
  }
}

.pager { margin-top: 24px; text-align: center; }

/* ========= 移动端适配 ========= */
@media (max-width: @bp-md) {
  .stepper {
    padding: 16px 12px;
    gap: 4px;

    .step {
      flex-direction: column;
      gap: 6px;
      text-align: center;

      .label { font-size: 12px; white-space: nowrap; }
      .line { display: none; }
    }
  }

  .search-bar {
    flex-wrap: wrap;

    .el-input { min-width: 100%; }
  }

  .proj-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .proj-card {
    padding: 16px;
    gap: 14px;

    .card-head {
      flex-direction: column;
      align-items: stretch;

      .status-tag {
        align-self: flex-start;
      }

      .prj-meta-row {
        gap: 6px;

        .prj-no-inline {
          em {
            max-width: 180px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }

      .prj-name {
        font-size: 13px;
        line-height: 1.6;
      }
    }

    .card-stats {
      grid-template-columns: 1fr;
      gap: 14px;
      padding: 14px;

      .stat-amount {
        .amount-value {
          .num { font-size: 20px; }
        }
      }
    }

    .card-foot {
      .apply-btn {
        width: 100%;
      }
    }
  }

  .filter-panel {
    padding: 12px 16px;

    .filter-row {
      flex-direction: column;
      gap: 10px;

      label {
        width: auto;
        padding-top: 0;
      }
    }
  }

  .confirm-card, .pay-card {
    padding: 20px 16px;

    h3 { font-size: 16px; margin-bottom: 16px; }
  }

  .confirm-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .confirm-foot, .pay-foot {
    flex-direction: column-reverse;
    align-items: stretch;

    .g-btn {
      width: 100%;
    }
  }

  .pay-card {
    .qr-box {
      padding: 16px;

      img { width: 180px; height: 180px; }
      .amount { font-size: 20px; }
    }
  }
}

@media (max-width: @bp-sm) {
  .proj-page { padding: 12px 0 32px; }

  .stepper {
    .step {
      .num { width: 24px; height: 24px; font-size: 12px; }
      .label { font-size: 11px; }
    }
  }

  .proj-card {
    padding: 14px 12px;

    .card-head {
      .prj-name { font-size: 14px; }
    }

    .card-stats {
      padding: 12px;
    }

    .card-foot {
      padding-top: 12px;
    }
  }
}
</style>
